/// <amd-module name="Feed/Legacy/Protocols/PyPi/PyPiDataService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";

import { Package } from "Feed/Common/Contracts/Feed";
import * as VSS_PyPiApi_Contracts from "Feed/Common/Protocols/PyPi/Contracts/PyPiApi";
import { IPyPiApiRestClient } from "Feed/Common/Protocols/PyPi/RestClient/PyPiApi";
import { MinimalPackageDetails, UpstreamingBehavior } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { BatchExecutor, IBatchIterator } from "Feed/Legacy/Protocols/Common/BatchHandling";
import { Feed, projId } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export interface IFeedPyPiDataService extends IVssService {
    readonly PyPiApiRestClient: IPyPiApiRestClient;
    readonly batchSize: number;
    batchDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void>;
    batchRestoreToFeed(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void>;
    batchPermanentlyDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void>;
    batchPromote(feed: Feed, viewId: string, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void>;
    getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior>;
    setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void>;
}

class PyPiDataService extends VssService implements IFeedPyPiDataService {
    get PyPiApiRestClient(): IPyPiApiRestClient {
        return this.pageContext.getRestClient<IPyPiApiRestClient>("PyPiApi.IPyPiApiRestClient");
    }

    get batchSize(): number {
        return this.pageContext
            .getService<HubWebPageDataService>("IFeedWebPageDataService")
            .getBatchOperationPageSize();
    }

    public async batchDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void> {
        await BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <VSS_PyPiApi_Contracts.PyPiPackagesBatchRequest>{
                data: null,
                operation: VSS_PyPiApi_Contracts.PyPiBatchOperationType.Delete,
                packages: packageDetails
            };
            await this.PyPiApiRestClient.updatePackageVersions(batchRequest, feed.id, projId(feed));
        });
    }

    public async batchRestoreToFeed(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void> {
        await BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <VSS_PyPiApi_Contracts.PyPiPackagesBatchRequest>{
                data: null,
                operation: VSS_PyPiApi_Contracts.PyPiBatchOperationType.RestoreToFeed,
                packages: packageDetails
            };
            await this.PyPiApiRestClient.updateRecycleBinPackageVersions(batchRequest, feed.id, projId(feed));
        });
    }

    public async batchPermanentlyDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void> {
        await BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <VSS_PyPiApi_Contracts.PyPiPackagesBatchRequest>{
                data: null,
                operation: VSS_PyPiApi_Contracts.PyPiBatchOperationType.PermanentDelete,
                packages: packageDetails
            };
            await this.PyPiApiRestClient.updateRecycleBinPackageVersions(batchRequest, feed.id, projId(feed));
        });
    }

    public async batchPromote(
        feed: Feed,
        viewId: string,
        iterator: IBatchIterator<MinimalPackageDetails[]>
    ): Promise<void> {
        await BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <VSS_PyPiApi_Contracts.PyPiPackagesBatchRequest>{
                data: { viewId },
                operation: VSS_PyPiApi_Contracts.PyPiBatchOperationType.Promote,
                packages: packageDetails
            };
            await this.PyPiApiRestClient.updatePackageVersions(batchRequest, feed.id, projId(feed));
        });
    }

    public getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior> {
        return this.PyPiApiRestClient.getUpstreamingBehavior(feed.id, pkg.name, projId(feed));
    }

    public setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void> {
        return this.PyPiApiRestClient.setUpstreamingBehavior(feed.id, pkg.name, behavior, projId(feed));
    }
}

Services.add("IFeedPyPiDataService", { serviceFactory: PyPiDataService });
